import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Search, ChevronRight, Filter, X,
  MessageSquare, Check, ChevronLeft, MoreHorizontal, AlertCircle, Edit, Play, TrendingUp, DollarSign,
  FileText, Trash2, Wallet
} from 'lucide-react';
import DemoSidebar from '../components/DemoSidebar';
import DemoTopBar from '../components/DemoTopBar';
import { useTranslation } from 'react-i18next';
import { TradingViewWidget } from '../components/TradingViewWidget';

interface Trade {
  id: number;
  pairs: string;
  direction: 'LONG' | 'SHORT';
  entry_price: string;
  exit_price: string | null;
  position_size: string;
  pnl_amount: string;
  pnl_percentage: string | null;
  status: 'WIN' | 'LOSS';
  trade_date: string;
  leverage: number | null;
  session: string | null;
  primary_setup_type: string | null;
  remarks: string | null;
  timeframe_analysis?: string | null;
  market_cap?: string | null;
  key_indicators?: string | null;
  fee?: string | null;
  commission?: string | null;
}

const formatCurrency = (value: string | number): string => {
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;
  if (isNaN(numValue)) return '$0.00';
  const absValue = Math.abs(numValue);
  const formatted = absValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return numValue < 0 ? `-$${formatted}` : `$${formatted}`;
};

const getTradingViewSymbol = (pairs: string) => {
  const cleanPairs = pairs.replace(/[\s/]/g, '').toUpperCase();
  return `BINANCE:${cleanPairs}`;
};

const StatRow = ({ label, value, valueColor, fontMono = false }: { label: string, value: any, valueColor?: string, fontMono?: boolean }) => (
  <div className="flex justify-between items-center px-4 py-3 text-[11px] border-b border-neutral-900 last:border-b-0">
    <span className="text-neutral-500 font-bold uppercase tracking-wider">{label}</span>
    <span className={`font-black ${valueColor || 'text-white'} ${fontMono ? 'font-mono' : ''}`}>{value}</span>
  </div>
);

const TradesDemo: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 1-minute timer to redirect to pricing page from demo mode
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/pricing');
    }, 60000);

    return () => clearTimeout(timer);
  }, [navigate]);

  const [selectedTrade, setSelectedTrade] = useState<Trade | null>(null);
  const [leftTab, setLeftTab] = useState<'Stats' | 'Strategy' | 'Executions'>('Stats');
  const [activeModal, setActiveModal] = useState<'chart' | 'notes' | 'pnl' | null>(null);
  const [filterSide, setFilterSide] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const TRADES_PER_PAGE = 15;
  const [replayTrade, setReplayTrade] = useState<Trade | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [panelWidth, setPanelWidth] = useState(800);
  const [panelOffset, setPanelOffset] = useState(0);

  const [trades, setTrades] = useState<Trade[]>([
    {
      id: 1,
      pairs: 'BTC/USDT',
      direction: 'LONG',
      entry_price: '64120.45',
      exit_price: '67120.45',
      position_size: '1.40',
      pnl_amount: '4200.00',
      pnl_percentage: '4.68',
      status: 'WIN',
      trade_date: '2024-09-09T14:32:00.000Z',
      leverage: 20,
      session: 'US Session',
      primary_setup_type: 'Breakout',
      remarks: 'Awesome trend continuation breakout. Caught the wave beautifully.',
      timeframe_analysis: '4H Chart',
      market_cap: 'Large Cap',
      key_indicators: 'RSI, MACD, EMA 20',
      fee: '15.42',
      commission: '2.50'
    },
    {
      id: 2,
      pairs: 'ETH/USDT',
      direction: 'SHORT',
      entry_price: '2410.15',
      exit_price: '2416.95',
      position_size: '12.50',
      pnl_amount: '-85.00',
      pnl_percentage: '-0.28',
      status: 'LOSS',
      trade_date: '2024-09-07T08:15:00.000Z',
      leverage: 10,
      session: 'Asian Session',
      primary_setup_type: 'Resistance Reject',
      remarks: 'Slightly stopped out before rejection started. Bad timing.',
      timeframe_analysis: '1H Chart',
      market_cap: 'Large Cap',
      key_indicators: 'Bollinger Bands',
      fee: '4.50',
      commission: '0.80'
    },
    {
      id: 3,
      pairs: 'SOL/USDT',
      direction: 'LONG',
      entry_price: '135.00',
      exit_price: '145.50',
      position_size: '200.00',
      pnl_amount: '2100.00',
      pnl_percentage: '7.78',
      status: 'WIN',
      trade_date: '2024-09-05T18:45:00.000Z',
      leverage: 5,
      session: 'US Session',
      primary_setup_type: 'Support Bounce',
      remarks: 'Daily support bounce play. Solid risk-reward ratio.',
      timeframe_analysis: 'Daily Chart',
      market_cap: 'Mid Cap',
      key_indicators: 'Stochastic RSI',
      fee: '8.20',
      commission: '1.20'
    },
    {
      id: 4,
      pairs: 'BTC/USDT',
      direction: 'LONG',
      entry_price: '58200.00',
      exit_price: '58556.00',
      position_size: '2.50',
      pnl_amount: '890.00',
      pnl_percentage: '0.61',
      status: 'WIN',
      trade_date: '2024-09-04T09:20:00.000Z',
      leverage: 15,
      session: 'European Session',
      primary_setup_type: 'EMA Cross',
      remarks: 'Quick scalp on EMA crossover on 15m chart.',
      timeframe_analysis: '15m Chart',
      market_cap: 'Large Cap',
      key_indicators: 'EMA 9/21 Crossover',
      fee: '12.00',
      commission: '1.80'
    },
    {
      id: 5,
      pairs: 'SOL/USDT',
      direction: 'SHORT',
      entry_price: '138.00',
      exit_price: '142.20',
      position_size: '100.00',
      pnl_amount: '-420.00',
      pnl_percentage: '-3.04',
      status: 'LOSS',
      trade_date: '2024-09-02T11:10:00.000Z',
      leverage: 10,
      session: 'European Session',
      primary_setup_type: 'Liquidity Sweep',
      remarks: 'Short execution on sweep. Ran into buy wall.',
      timeframe_analysis: '30m Chart',
      market_cap: 'Mid Cap',
      key_indicators: 'Liquidity Levels',
      fee: '3.80',
      commission: '0.60'
    },
    {
      id: 6,
      pairs: 'BTC/USDT',
      direction: 'LONG',
      entry_price: '57400.00',
      exit_price: '58640.00',
      position_size: '1.00',
      pnl_amount: '1240.00',
      pnl_percentage: '2.16',
      status: 'WIN',
      trade_date: '2024-09-01T15:00:00.000Z',
      leverage: 10,
      session: 'US Session',
      primary_setup_type: 'Breakout',
      remarks: 'Beautiful breakout play on higher low confirmation.',
      timeframe_analysis: '1H Chart',
      market_cap: 'Large Cap',
      key_indicators: 'Volume Trend',
      fee: '6.50',
      commission: '1.00'
    }
  ]);

  const pnl = '+$7,685.00';
  const pnlPercent = '+6.54%';
  const pnlPositive = true;
  const winRate = '66.7%';
  const lossRate = 33.3;
  const profitFactor = 14.86;

  const filteredTrades = trades.filter((t) => {
    const matchSide = filterSide === 'All' || t.direction === filterSide.toUpperCase();
    const matchStatus = filterStatus === 'All' || t.status === filterStatus.toUpperCase();
    return matchSide && matchStatus;
  });

  const totalPages = Math.ceil(filteredTrades.length / TRADES_PER_PAGE);
  const paginatedTrades = filteredTrades.slice(
    (currentPage - 1) * TRADES_PER_PAGE,
    currentPage * TRADES_PER_PAGE
  );

  const handleShareTrade = (pairs: string, pnlVal: number) => {
    const mockLink = `https://catatcrypto.com/share/trade/${Math.random().toString(36).slice(2, 8)}`;
    navigator.clipboard.writeText(mockLink);
    setSuccessMessage(`Share link copied: ${pairs} (${pnlVal >= 0 ? '+' : ''}$${pnlVal.toFixed(2)})! Post it on Discord!`);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleSaveTradeRemarks = async (tradeId: number, updatedRemarks: string) => {
    setTrades(prev => prev.map(t => t.id === tradeId ? { ...t, remarks: updatedRemarks } : t));
    if (selectedTrade && selectedTrade.id === tradeId) {
      setSelectedTrade(prev => prev ? { ...prev, remarks: updatedRemarks } : null);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex">
      <DemoSidebar />

      <main className="flex-1 bg-[#050505] min-h-screen relative pt-16 md:pt-0 overflow-x-hidden">
        <DemoTopBar title="Trades Demo" />

        <div className="mx-auto w-full max-w-[1132px] pt-4 md:pt-8 px-3 md:px-0">
          <div className="relative overflow-hidden bg-gradient-to-r from-[#0B0C10] to-[#12131A] border border-[#EF4444]/20 rounded-3xl p-4 md:p-6 flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6 shadow-[0_0_50px_rgba(229,9,20,0.04)]">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#E50914]/5 rounded-full blur-[80px] pointer-events-none" />
            <div className="absolute -left-12 -bottom-12 w-48 h-48 bg-neutral-950 rounded-full blur-[60px] pointer-events-none" />
            
            <div className="flex items-center gap-4 relative z-10 font-sans">
              <div className="w-12 h-12 rounded-2xl bg-[#EF4444]/10 border border-[#EF4444]/20 flex items-center justify-center text-[#EF4444] shrink-0 shadow-[0_0_15px_rgba(239,68,68,0.1)]">
                <TrendingUp size={22} className="animate-pulse" />
              </div>
              <div>
                <h4 className="text-sm font-bold tracking-wide text-white">Preview Mode Active</h4>
                <p className="text-xs text-neutral-400 mt-1 max-w-xl">
                  This page displays the transaction history of the demo account. Register an account to upload real transaction data.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 shrink-0 relative z-10">
              <button 
                onClick={() => {
                  window.location.href = '/register';
                }}
                className="px-5 py-2.5 rounded-xl bg-[#E50914] hover:bg-[#E50914]/90 text-white text-xs font-bold transition-all shadow-lg shadow-red-950/20 hover:scale-105"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>

        <div className="p-3 md:p-8 max-w-[1132px] mx-auto w-full">
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-4 mb-6 md:mb-8">
            <StatCard
              title="NET PNL" value={pnl} percent={pnlPercent} positive={pnlPositive}
              chart={<PnlLineChart pnlPositive={pnlPositive} />}
            />
            <StatCard
              title="WIN RATE %" value={winRate}
              chart={<AnimatedCircle winRate={winRate} />}
            />
            <StatCard
              title="LOSS RATE %" value={`${lossRate.toFixed(1)}%`}
              chart={<AnimatedLossCircle lossRate={lossRate} />}
            />
            <StatCard
              title="PROFIT FACTOR" value={profitFactor.toFixed(2)}
              sub="Health: Optimal"
            />
          </div>

          <div className="flex flex-col gap-3 mb-6 font-sans">
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-3 w-full">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-1.5 w-full sm:w-auto">
                <span className="text-[11px] text-neutral-500 font-bold">Side:</span>
                <FilterDropdown
                  label="Side"
                  value={filterSide}
                  options={[
                    { id: 'All', label: 'All Sides' },
                    { id: 'Long', label: 'Long', color: '#3b82f6' },
                    { id: 'Short', label: 'Short', color: '#f59e0b' }
                  ]}
                  onChange={(v) => { setFilterSide(v); setCurrentPage(1); }}
                />
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-1.5 w-full sm:w-auto">
                <span className="text-[11px] text-neutral-500 font-bold">Status:</span>
                <FilterDropdown
                  label="Status"
                  value={filterStatus}
                  options={[
                    { id: 'All', label: 'All Statuses' },
                    { id: 'Win', label: 'Wins', color: '#10b981' },
                    { id: 'Loss', label: 'Losses', color: '#ef4444' }
                  ]}
                  onChange={(v) => { setFilterStatus(v); setCurrentPage(1); }}
                />
              </div>
            </div>
            
            <div className="flex items-center justify-between min-h-[28px] flex-wrap gap-y-2 gap-x-2">
              <div className="flex items-center gap-1.5 flex-wrap">
                <AnimatePresence>
                  {filterSide !== 'All' && (
                    <motion.div key="chip-side" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-1 px-2.5 py-1 bg-red-500/10 border border-red-500/30 text-red-400 text-[10px] font-bold rounded-full">
                      Side: {filterSide}
                      <button onClick={() => { setFilterSide('All'); setCurrentPage(1); }} className="ml-0.5 hover:text-white transition-colors">×</button>
                    </motion.div>
                  )}
                  {filterStatus !== 'All' && (
                    <motion.div key="chip-status" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center gap-1 px-2.5 py-1 bg-red-500/10 border border-red-500/30 text-red-400 text-[10px] font-bold rounded-full">
                      Status: {filterStatus}
                      <button onClick={() => { setFilterStatus('All'); setCurrentPage(1); }} className="ml-0.5 hover:text-white transition-colors">×</button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider">Total: {filteredTrades.length} trades</span>
                <span className="text-neutral-800">|</span>
                <button
                  onClick={() => {
                    setFilterSide('All');
                    setFilterStatus('All');
                    setCurrentPage(1);
                  }}
                  className="text-[10px] font-black uppercase tracking-widest text-neutral-500 hover:text-white transition-colors"
                >
                  Reset Filters
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto rounded-2xl -mx-3 md:mx-0 scrollbar-hide font-sans">
            <div className="bg-[#111] border border-neutral-800 rounded-2xl overflow-hidden min-w-[900px]">
              <table className="w-full text-left border-collapse">
                <thead className="bg-[#0A0A0A] text-[10px] uppercase font-black text-neutral-500 tracking-widest border-b border-neutral-800">
                  <tr>
                    <th className="px-4 py-4">Status</th>
                    <th className="px-4 py-4">Source</th>
                    <th className="px-4 py-4">Date / Time</th>
                    <th className="px-4 py-4">Asset</th>
                    <th className="px-4 py-4 text-right">Entry / Exit</th>
                    <th className="px-4 py-4 text-right">Size</th>
                    <th className="px-4 py-4 text-right">Return</th>
                    <th className="px-4 py-4">Side</th>
                    <th className="px-4 py-4">Setup</th>
                    <th className="px-4 py-4"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800/50">
                  {filteredTrades.length > 0 ? (
                    paginatedTrades.map((trade) => (
                      <tr
                        key={trade.id}
                        className="hover:bg-neutral-800/30 transition-all group"
                      >
                        <td className="px-4 py-4">
                          <span className={`text-[10px] font-black px-2 py-0.5 rounded ${trade.status === 'WIN' ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                            {trade.status}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-neutral-500 font-bold">DEMO ACCOUNT</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-xs font-bold text-white font-mono">
                            {new Date(trade.trade_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </div>
                          <div className="text-[10px] text-neutral-500 mt-0.5 font-mono">
                            {new Date(trade.trade_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-xs font-black text-white">{trade.pairs}</td>
                        <td className="px-4 py-4 text-right">
                          <div className="text-xs font-bold text-neutral-300 font-mono">
                            ${parseFloat(trade.entry_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div className="text-[10px] text-neutral-500 mt-0.5 font-mono">
                            {trade.exit_price ? `$${parseFloat(trade.exit_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-right text-xs font-bold text-neutral-400 font-mono">
                          {trade.position_size} {trade.pairs.split('/')[0]}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className={`text-xs font-black font-mono ${trade.status === 'WIN' ? 'text-green-500' : 'text-red-500'}`}>
                            {formatCurrency(trade.pnl_amount)}
                          </div>
                          <div className={`text-[9px] font-black font-mono mt-0.5 ${trade.status === 'WIN' ? 'text-green-600/60' : 'text-red-600/60'}`}>
                            {trade.pnl_percentage ? `${parseFloat(trade.pnl_percentage) >= 0 ? '+' : ''}${trade.pnl_percentage}%` : '—'}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`text-[9px] font-black tracking-widest uppercase ${trade.direction === 'LONG' ? 'text-blue-500' : 'text-amber-500'}`}>
                            {trade.direction}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-[10px] font-bold text-neutral-500 uppercase">
                          {trade.primary_setup_type || 'Manual'}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex items-center justify-end gap-3">
                            {trade.remarks && <MessageSquare size={14} className="text-neutral-600" />}
                            <button
                              onClick={() => setSelectedTrade(trade)}
                              className="p-1.5 hover:bg-neutral-800 rounded-lg transition-colors text-neutral-400 hover:text-white"
                            >
                              <ChevronRight size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="px-4 py-12 text-center text-neutral-600 text-xs font-bold">
                        No transactions found matching the filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {filteredTrades.length > 0 && (
            <div className="flex items-center justify-between px-2 py-4 font-sans">
              <span className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider">
                Showing {(currentPage - 1) * TRADES_PER_PAGE + 1}–{Math.min(currentPage * TRADES_PER_PAGE, filteredTrades.length)} of {filteredTrades.length} trades
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center bg-[#1a1a1a] border border-neutral-800 text-neutral-400 hover:text-white transition-all ${currentPage === 1 ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-lg text-xs font-bold flex items-center justify-center transition-all ${currentPage === page ? 'bg-red-600 text-white' : 'bg-[#1a1a1a] border border-neutral-800 text-neutral-400 hover:text-white'}`}
                  >{page}</button>
                ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center bg-[#1a1a1a] border border-neutral-800 text-neutral-400 hover:text-white transition-all ${currentPage === totalPages ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>

        <AnimatePresence>
          {selectedTrade && (
            <>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={() => setSelectedTrade(null)}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              />
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: panelOffset }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                style={{ width: window.innerWidth < 768 ? '100%' : `${panelWidth}px` }}
                className="fixed right-0 top-0 bottom-0 h-full bg-[#0A0A0A] border-l border-neutral-800 z-50 shadow-2xl flex flex-col font-sans"
              >
                {/* Left Resizing Handle */}
                <div
                  className="absolute top-0 bottom-0 left-0 w-1.5 cursor-col-resize hover:bg-[#E50914]/50 active:bg-[#E50914] transition-colors z-[60]"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const startX = e.clientX;
                    const startWidth = panelWidth;
                    const handleMouseMove = (moveEvent: MouseEvent) => {
                      const deltaX = startX - moveEvent.clientX;
                      const newWidth = Math.max(320, Math.min(window.innerWidth - 80, startWidth + deltaX));
                      setPanelWidth(newWidth);
                    };
                    const handleMouseUp = () => {
                      document.removeEventListener('mousemove', handleMouseMove);
                      document.removeEventListener('mouseup', handleMouseUp);
                    };
                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                  }}
                />
                <div 
                  className="px-4 md:px-8 py-3 md:py-5 border-b border-neutral-800 bg-[#0F0F0F] shrink-0 cursor-grab active:cursor-grabbing select-none"
                  onMouseDown={(e) => {
                    if ((e.target as HTMLElement).closest('button') || (e.target as HTMLElement).closest('a')) return;
                    e.preventDefault();
                    const startX = e.clientX;
                    const startOffset = panelOffset;
                    const handleMouseMove = (moveEvent: MouseEvent) => {
                      const deltaX = moveEvent.clientX - startX;
                      const newOffset = Math.max(-50, Math.min(window.innerWidth, startOffset + deltaX));
                      setPanelOffset(newOffset);
                    };
                    const handleMouseUp = (moveEvent: MouseEvent) => {
                      document.removeEventListener('mousemove', handleMouseMove);
                      document.removeEventListener('mouseup', handleMouseUp);
                      const diffX = moveEvent.clientX - startX;
                      if (diffX > 150) {
                        setSelectedTrade(null);
                        setPanelOffset(0);
                      } else {
                        setPanelOffset(0);
                      }
                    };
                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <button 
                        onClick={() => setSelectedTrade(null)} 
                        className="p-2 hover:bg-neutral-800 rounded-xl transition-colors text-neutral-400 hover:text-white"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <div>
                        <div className="flex items-center gap-3">
                          <h2 className="text-xl font-black text-white tracking-tight flex items-center gap-2">
                            {selectedTrade.pairs}
                          </h2>
                          <span className={`text-[10px] font-black px-2 py-0.5 rounded tracking-wide uppercase ${
                            selectedTrade.status === 'WIN' ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'
                          }`}>
                            {selectedTrade.status}
                          </span>
                          <span className={`text-[9px] font-black tracking-widest uppercase px-2 py-0.5 rounded ${
                            selectedTrade.direction === 'LONG' ? 'bg-blue-500/10 text-blue-500 border border-blue-500/20' : 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                          }`}>
                            {selectedTrade.direction}
                          </span>
                        </div>
                        <p className="text-[11px] text-neutral-500 font-bold mt-1 tracking-wide uppercase font-mono">
                          {new Date(selectedTrade.trade_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => setReplayTrade(selectedTrade)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-neutral-900 border border-neutral-800 rounded-lg hover:bg-neutral-800 text-[11px] font-bold text-neutral-300 transition-all"
                      >
                        <Play size={12} className="text-blue-500 fill-blue-500 animate-pulse" />
                        <span>Replay</span>
                      </button>
                      <button 
                        onClick={() => handleShareTrade(selectedTrade.pairs, parseFloat(selectedTrade.pnl_amount))}
                        className="flex items-center gap-2 px-3 py-1.5 bg-neutral-900 border border-neutral-800 rounded-lg hover:bg-neutral-800 text-[11px] font-bold text-neutral-300 transition-all"
                      >
                        <Share2Icon size={12} className="text-purple-500" />
                        <span>Share</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex-1 w-full flex flex-col md:flex-row overflow-y-auto md:overflow-hidden">
                  <div className="w-full md:w-[380px] md:max-h-none border-b md:border-b-0 border-r-0 md:border-r border-neutral-800 bg-[#0F0F0F] flex flex-col overflow-hidden">
                    <div className="flex overflow-x-auto scrollbar-hide border-b border-neutral-800 p-2 gap-1 bg-[#0A0A0A] shrink-0">
                      {['Stats', 'Strategy', 'Executions'].map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setLeftTab(tab as any)}
                          className={`flex-shrink-0 px-4 py-2 text-[11px] font-black uppercase tracking-wider rounded-lg transition-all ${
                            leftTab === tab 
                              ? 'bg-neutral-900 text-white shadow' 
                              : 'text-neutral-500 hover:text-neutral-300'
                          }`}
                        >
                          {tab}
                        </button>
                      ))}
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 md:space-y-6">
                      {leftTab === 'Stats' && (
                        <div className="space-y-4 md:space-y-6">
                          <div className={`p-4 md:p-5 bg-neutral-900/50 rounded-xl border border-neutral-800 border-l-4 ${
                            selectedTrade.status === 'WIN' ? 'border-l-green-500' : 'border-l-red-500'
                          }`}>
                            <p className="text-[10px] font-black uppercase text-neutral-500 tracking-widest mb-1.5">Net P&L</p>
                            <h3 className={`text-3xl font-black tracking-tight font-mono ${
                              selectedTrade.status === 'WIN' ? 'text-green-500' : 'text-red-500'
                            }`}>
                              {parseFloat(selectedTrade.pnl_amount) >= 0 ? '+' : ''}{formatCurrency(selectedTrade.pnl_amount)}
                            </h3>
                          </div>

                          <div className="space-y-3">
                            <h4 className="text-[10px] font-black uppercase text-neutral-500 tracking-wider">Trade Properties</h4>
                            <div className="bg-neutral-950/45 rounded-2xl border border-neutral-800/85 overflow-hidden">
                              <StatRow label="Side" value={selectedTrade.direction} valueColor={selectedTrade.direction === 'LONG' ? 'text-blue-500' : 'text-amber-500'} />
                              <StatRow label="Account" value="DEMO ACCOUNT" />
                              <StatRow label="Entry Price" value={`$${parseFloat(selectedTrade.entry_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} fontMono />
                              <StatRow label="Exit Price" value={selectedTrade.exit_price ? `$${parseFloat(selectedTrade.exit_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'Open'} fontMono />
                              <StatRow label="Position Size" value={`${selectedTrade.position_size} ${selectedTrade.pairs.split('/')[0]}`} fontMono />
                              <StatRow label="Commissions & Fees" value={`$${parseFloat(selectedTrade.fee || '0').toFixed(2)}`} fontMono />
                              <StatRow label="Net ROI" value={selectedTrade.pnl_percentage ? `${selectedTrade.pnl_percentage}%` : '-'} fontMono valueColor={selectedTrade.status === 'WIN' ? 'text-green-500' : 'text-red-500'} />
                            </div>
                          </div>
                        </div>
                      )}

                      {leftTab === 'Strategy' && (
                        <div className="space-y-4">
                          <h4 className="text-[10px] font-black uppercase text-neutral-500 tracking-wider">Setup Strategy</h4>
                          <div className="bg-neutral-900/30 p-4 md:p-5 rounded-xl md:rounded-2xl border border-neutral-800 space-y-3 md:space-y-4">
                            <div>
                              <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Primary Setup</p>
                              <p className="text-xs text-white font-bold uppercase tracking-wider">{selectedTrade.primary_setup_type || 'Breakout'}</p>
                            </div>
                            <div>
                              <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Timeframe Analysis</p>
                              <p className="text-xs text-white font-bold">{selectedTrade.timeframe_analysis || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Trading Session</p>
                              <p className="text-xs text-white font-bold uppercase tracking-wide">{selectedTrade.session || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Market Cap Class</p>
                              <p className="text-xs text-white font-bold uppercase">{selectedTrade.market_cap || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Key Indicators</p>
                              <p className="text-xs text-neutral-300 leading-relaxed font-bold">{selectedTrade.key_indicators || 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {leftTab === 'Executions' && (
                        <div className="space-y-4">
                          <h4 className="text-[10px] font-black uppercase text-neutral-500 tracking-wider">Execution Log</h4>
                          <div className="bg-neutral-900/30 p-4 md:p-5 rounded-xl md:rounded-2xl border border-neutral-800 space-y-3">
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-neutral-500 font-bold">Filled Date</span>
                              <span className="text-neutral-300 font-mono">{new Date(selectedTrade.trade_date).toLocaleDateString()}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-neutral-500 font-bold">Direction</span>
                              <span className={`font-bold uppercase ${selectedTrade.direction === 'LONG' ? 'text-blue-500' : 'text-amber-500'}`}>{selectedTrade.direction}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-neutral-500 font-bold">Quantity</span>
                              <span className="text-neutral-300 font-mono">{selectedTrade.position_size}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex-1 bg-black flex flex-col min-h-[300px] overflow-hidden relative">
                    <div className="absolute inset-0 z-0">
                      <TradingViewWidget
                        symbol={getTradingViewSymbol(selectedTrade.pairs)}
                        containerId={`tv_chart_demo_${selectedTrade.id}`}
                      />
                    </div>
                    <div className="absolute top-4 left-4 z-10 bg-neutral-900/90 backdrop-blur border border-neutral-800 p-2.5 rounded-xl text-[10px] font-black tracking-widest uppercase text-neutral-400 select-none">
                      Interactive Live Chart Preview
                    </div>
                  </div>
                </div>

                <div className="px-4 md:px-8 py-4 border-t border-neutral-800 bg-[#0F0F0F] shrink-0">
                  <TradeRemarksEditor
                    tradeId={selectedTrade.id}
                    initialRemarks={selectedTrade.remarks || ''}
                    onSave={handleSaveTradeRemarks}
                  />
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {replayTrade && (
            <ReplaySimulator
              trade={replayTrade}
              onClose={() => setReplayTrade(null)}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[10002] bg-[#0E0E0E]/95 border border-green-500/30 rounded-2xl px-6 py-4 shadow-2xl backdrop-blur-md flex items-center gap-3 text-white"
            >
              <Check size={18} className="text-green-500" />
              <span className="text-xs font-bold">{successMessage}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

const StatCard = ({ title, value, percent, positive, sub, chart }: any) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="border rounded-2xl p-4 md:p-6 flex flex-col justify-between transition-all bg-[#111] border-neutral-800 hover:border-neutral-700 h-full font-sans"
    >
      <div className="flex justify-between items-start mb-4 min-w-0">
        <p className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-neutral-500">{title}</p>
        {chart && <div className="flex-shrink-0">{chart}</div>}
      </div>
      <div>
        <h3 className="text-base md:text-2.5xl font-black tracking-tighter truncate text-white">
          {value}
        </h3>
        {percent && <p className={`text-[10px] font-black mt-1 ${positive ? 'text-green-500' : 'text-red-500'}`}>{percent} since last week</p>}
        {sub && <p className="text-[10px] font-bold mt-1 uppercase tracking-tight text-neutral-600">{sub}</p>}
      </div>
    </motion.div>
  );
};

const PnlLineChart = ({ pnlPositive }: { pnlPositive: boolean }) => {
  const stroke = pnlPositive ? '#22c55e' : '#ef4444';
  const gradColor = pnlPositive ? '#22c55e' : '#ef4444';

  return (
    <svg className="w-24 h-12" viewBox="0 0 100 40" preserveAspectRatio="none">
      <defs>
        <linearGradient id="pnlGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={gradColor} />
          <stop offset="100%" stopColor="transparent" />
        </linearGradient>
      </defs>
      <path
        d="M0 35 L20 25 L40 30 L60 10 L80 15 L100 5"
        fill="none"
        stroke={stroke}
        strokeWidth="2"
      />
      <path d="M0 35 L20 25 L40 30 L60 10 L80 15 L100 5 L100 40 L0 40 Z" fill="url(#pnlGrad)" opacity="0.2" />
    </svg>
  );
};

const AnimatedCircle = ({ winRate }: { winRate: string }) => {
  const target = parseFloat(winRate) || 0;
  return (
    <div className="w-10 h-10 md:w-12 md:h-12 relative flex-shrink-0">
      <svg className="w-full h-full" viewBox="0 0 36 36">
        <circle cx="18" cy="18" r="16" fill="none" stroke="#262626" strokeWidth="3" />
        <circle
          cx="18" cy="18" r="16" fill="none" stroke="#22c55e" strokeWidth="3"
          strokeDasharray={`${target}, 100`}
          strokeLinecap="round"
        />
        <text x="18" y="21" textAnchor="middle" fontSize="8" fontWeight="900" fill="#22c55e" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {Math.round(target)}%
        </text>
      </svg>
    </div>
  );
};

const AnimatedLossCircle = ({ lossRate }: { lossRate: number }) => {
  return (
    <div className="w-10 h-10 md:w-12 md:h-12 relative flex-shrink-0">
      <svg className="w-full h-full" viewBox="0 0 36 36">
        <circle cx="18" cy="18" r="16" fill="none" stroke="#262626" strokeWidth="3" />
        <circle
          cx="18" cy="18" r="16" fill="none" stroke="#ef4444" strokeWidth="3"
          strokeDasharray={`${lossRate}, 100`}
          strokeLinecap="round"
        />
        <text x="18" y="21" textAnchor="middle" fontSize="8" fontWeight="900" fill="#ef4444" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {Math.round(lossRate)}%
        </text>
      </svg>
    </div>
  );
};

interface FilterDropdownProps {
  label: string;
  value: string;
  options: { id: string; label: string; color?: string }[];
  onChange: (id: string) => void;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({ label, value, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const selectedOpt = options.find(o => o.id === value) || options[0];

  return (
    <div ref={ref} className="relative z-30">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all flex items-center gap-1.5 bg-[#1a1a1a] border border-neutral-800 text-white hover:bg-neutral-800"
      >
        <span className="text-neutral-600 font-normal">{label}:</span>
        <span className="text-white">{selectedOpt.label}</span>
        <ChevronRight size={12} className={`transition-transform duration-200 ${isOpen ? 'rotate-90' : ''} text-neutral-600`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="absolute top-full left-0 mt-2 rounded-xl overflow-hidden shadow-2xl p-1 space-y-0.5 bg-[#111] border border-neutral-800 w-44"
          >
            {options.map((opt) => (
              <button
                key={opt.id}
                onClick={() => {
                  onChange(opt.id);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-xs font-bold transition-all ${
                  value === opt.id ? 'bg-neutral-800 text-white' : 'text-neutral-400 hover:bg-neutral-900 hover:text-white'
                }`}
              >
                {opt.color && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: opt.color }} />}
                <span>{opt.label}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const TradeRemarksEditor = ({ tradeId, initialRemarks, onSave }: { tradeId: number, initialRemarks: string, onSave: (id: number, remarks: string) => Promise<void> }) => {
  const [noteText, setNoteText] = useState(initialRemarks);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-between items-center">
        <h4 className="text-[10px] font-black uppercase text-neutral-500 tracking-wider">Remarks / Notes</h4>
        <button
          onClick={async () => {
            setSaving(true);
            await onSave(tradeId, noteText);
            setSaving(false);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
          }}
          className="px-3 py-1 bg-neutral-900 hover:bg-neutral-800 text-white border border-neutral-800 hover:border-neutral-700 text-[10px] font-bold rounded-lg transition-all"
        >
          {saving ? 'Saving...' : saved ? 'Saved ✓' : 'Save'}
        </button>
      </div>
      <textarea
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
        className="w-full bg-neutral-950 border border-neutral-800 rounded-xl p-4 text-xs text-neutral-300 focus:outline-none focus:border-neutral-700 leading-relaxed font-sans resize-none h-24"
        placeholder="Add observations or notes on this trade execution..."
      />
    </div>
  );
};

interface ReplaySimulatorProps {
  trade: Trade;
  onClose: () => void;
}

const ReplaySimulator: React.FC<ReplaySimulatorProps> = ({ trade, onClose }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [speed, setSpeed] = useState(800);

  const isWin = trade.status === 'WIN';
  const isLong = trade.direction === 'LONG';
  const entryPrice = parseFloat(trade.entry_price) || 100;
  const exitPrice = parseFloat(trade.exit_price || '') || (isWin ? entryPrice * 1.05 : entryPrice * 0.95);
  
  const candles = React.useMemo(() => {
    const list = [];
    let price = entryPrice * 0.98;
    
    for (let i = 0; i < 15; i++) {
      let open = price;
      let close = price;
      let event = '';

      if (i < 4) {
        close = open + (entryPrice - open) * (i / 4);
        event = i === 3 ? 'ENTRY_TRIGGERED' : '';
      } else if (i < 11) {
        if (isWin) {
          close = open + (exitPrice - entryPrice) * ((i - 3) / 8) + (Math.random() - 0.5) * (entryPrice * 0.005);
        } else {
          close = open - (entryPrice - exitPrice) * ((i - 3) / 8) + (Math.random() - 0.5) * (entryPrice * 0.005);
        }
      } else if (i < 14) {
        close = exitPrice;
        event = i === 13 ? 'EXIT_TRIGGERED' : '';
      } else {
        close = open + (Math.random() - 0.5) * (entryPrice * 0.005);
      }

      const high = Math.max(open, close) + (Math.random() * (entryPrice * 0.003));
      const low = Math.min(open, close) - (Math.random() * (entryPrice * 0.003));
      price = close;

      list.push({
        open,
        close,
        high,
        low,
        isGreen: close >= open,
        event
      });
    }
    return list;
  }, [isWin, entryPrice, exitPrice]);

  useEffect(() => {
    if (!isPlaying) return;

    const timer = setInterval(() => {
      setCurrentStep(prev => {
        if (prev < candles.length - 1) {
          return prev + 1;
        } else {
          setIsPlaying(false);
          return prev;
        }
      });
    }, speed);

    return () => clearInterval(timer);
  }, [isPlaying, speed, candles.length]);

  const activeCandle = candles[currentStep];
  const currentPrice = activeCandle ? activeCandle.close : entryPrice;
  
  let runningPnl = 0;
  const size = parseFloat(trade.position_size) || 1;
  
  if (currentStep >= 3) {
    if (isLong) {
      runningPnl = (currentPrice - entryPrice) * size;
    } else {
      runningPnl = (entryPrice - currentPrice) * size;
    }
  }
  
  if (currentStep >= 13) {
    runningPnl = parseFloat(trade.pnl_amount);
  }

  return (
    <div className="fixed inset-0 z-[10005] bg-black/95 backdrop-blur-xl flex flex-col font-sans text-white select-none">
      <div className="px-8 py-5 border-b border-neutral-900 flex justify-between items-center bg-[#050505] shrink-0">
        <div className="flex items-center gap-4">
          <span className="px-3 py-1 bg-blue-500/10 border border-blue-500/20 text-blue-500 text-[10px] font-black uppercase tracking-widest rounded-lg">
            PREMIUM REPLAY
          </span>
          <h2 className="text-lg font-black tracking-tight">{trade.pairs} - Trade Candle Replay</h2>
        </div>
        <button 
          onClick={onClose}
          className="p-1.5 hover:bg-neutral-900 border border-neutral-800 rounded-xl transition-all text-neutral-500 hover:text-white"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 p-8 flex flex-col gap-6 overflow-y-auto max-w-5xl mx-auto w-full">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 shrink-0">
          <div className="bg-neutral-900/40 border border-neutral-800/80 rounded-2xl p-5">
            <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Status</p>
            <p className="text-xs font-black uppercase tracking-wider text-neutral-400 font-mono">
              {currentStep < 3 ? 'PRE-ENTRY CONSOLIDATION' : currentStep < 13 ? 'ACTIVE POSITION LIVE' : 'TRADE COMPLETED'}
            </p>
          </div>
          <div className="bg-neutral-900/40 border border-neutral-800/80 rounded-2xl p-5">
            <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Current Price</p>
            <p className="text-lg font-bold font-mono text-white">${currentPrice.toFixed(2)}</p>
          </div>
          <div className="bg-neutral-900/40 border border-neutral-800/80 rounded-2xl p-5">
            <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Entry Price</p>
            <p className="text-lg font-bold font-mono text-neutral-400">${entryPrice.toFixed(2)}</p>
          </div>
          <div className="bg-neutral-900/40 border border-neutral-800/80 rounded-2xl p-5">
            <p className="text-[9px] font-black uppercase text-neutral-500 tracking-widest mb-1">Running P&L</p>
            <p className={`text-lg font-black font-mono ${runningPnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {runningPnl >= 0 ? '+' : ''}${runningPnl.toFixed(2)}
            </p>
          </div>
        </div>

        <div className="h-96 bg-neutral-950 border border-neutral-900 rounded-3xl p-8 relative flex items-end justify-between overflow-hidden shadow-2xl shrink-0">
          <div className="absolute inset-x-0 top-1/4 h-px bg-neutral-900/10" />
          <div className="absolute inset-x-0 top-2/4 h-px bg-neutral-900/10" />
          <div className="absolute inset-x-0 top-3/4 h-px bg-neutral-900/10" />

          <div className="absolute inset-x-0 top-[60%] h-px border-t border-dashed border-blue-500/30 flex justify-between px-4 z-10">
            <span className="text-[9px] font-bold text-blue-500 uppercase tracking-widest bg-black px-1.5 py-0.5 rounded border border-blue-500/20 -translate-y-1/2">
              Entry Level: ${entryPrice.toFixed(2)}
            </span>
          </div>
          <div className="absolute inset-x-0 top-[30%] h-px border-t border-dashed border-red-500/30 flex justify-between px-4 z-10">
            <span className="text-[9px] font-bold text-red-500 uppercase tracking-widest bg-black px-1.5 py-0.5 rounded border border-red-500/20 -translate-y-1/2">
              Exit Level: ${exitPrice.toFixed(2)}
            </span>
          </div>

          <div className="w-full h-64 flex items-end justify-between relative z-20 px-4 sm:px-8">
            {candles.map((candle, idx) => {
              const isRendered = idx <= currentStep;
              if (!isRendered) return <div key={idx} className="flex-1 opacity-0" />;

              const minPrice = entryPrice * 0.975;
              const maxPrice = entryPrice * 1.075;
              const diff = maxPrice - minPrice;
              
              const pctOpen = ((candle.open - minPrice) / diff) * 100;
              const pctClose = ((candle.close - minPrice) / diff) * 100;
              const pctHigh = ((candle.high - minPrice) / diff) * 100;
              const pctLow = ((candle.low - minPrice) / diff) * 100;

              const bottom = Math.min(pctOpen, pctClose);
              const height = Math.max(3, Math.abs(pctOpen - pctClose));
              const wickBottom = pctLow;
              const wickHeight = Math.max(1, pctHigh - pctLow);

              return (
                <div key={idx} className="flex-1 flex flex-col items-center justify-end h-full relative group">
                  {candle.event === 'ENTRY_TRIGGERED' && (
                    <div className="absolute bottom-[105%] bg-blue-500 text-black font-extrabold text-[8px] px-2 py-0.5 rounded-full uppercase tracking-wider animate-bounce z-30">
                      ENTRY
                    </div>
                  )}
                  {candle.event === 'EXIT_TRIGGERED' && (
                    <div className="absolute bottom-[105%] bg-red-500 text-white font-extrabold text-[8px] px-2 py-0.5 rounded-full uppercase tracking-wider animate-bounce z-30">
                      EXIT
                    </div>
                  )}

                  <div 
                    style={{ bottom: `${wickBottom}%`, height: `${wickHeight}%` }}
                    className={`absolute w-0.5 ${candle.isGreen ? 'bg-green-500' : 'bg-red-500'}`}
                  />

                  <div 
                    style={{ bottom: `${bottom}%`, height: `${height}%` }}
                    className={`w-3 sm:w-6 rounded-sm absolute ${candle.isGreen ? 'bg-green-500' : 'bg-red-500'} shadow-lg group-hover:scale-110 transition-all`}
                  />

                  <div className="absolute bottom-full mb-6 z-30 bg-neutral-900 border border-neutral-800 rounded-lg p-2 text-[9px] pointer-events-none opacity-0 group-hover:opacity-100 transition-all font-mono whitespace-nowrap flex flex-col gap-0.5">
                    <p className="text-white">O: {candle.open.toFixed(2)}</p>
                    <p className="text-white">C: {candle.close.toFixed(2)}</p>
                    <p className="text-white">H: {candle.high.toFixed(2)}</p>
                    <p className="text-white">L: {candle.low.toFixed(2)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-neutral-950 border border-neutral-900 rounded-3xl p-6 flex flex-col sm:flex-row justify-between items-center gap-6 shrink-0">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setCurrentStep(0)}
              className="px-4 py-2.5 bg-neutral-900 border border-neutral-800 hover:text-white rounded-xl text-[10px] font-black uppercase tracking-wider transition-all outline-none"
            >
              Reset
            </button>
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all outline-none ${isPlaying ? 'bg-amber-600 text-white hover:bg-amber-500' : 'bg-red-600 text-white hover:bg-red-500'} shadow-lg`}
            >
              {isPlaying ? 'Pause' : 'Play Simulation'}
            </button>
            <button
              onClick={() => setCurrentStep(prev => Math.min(candles.length - 1, prev + 1))}
              disabled={isPlaying}
              className="px-4 py-2.5 bg-neutral-900 border border-neutral-800 hover:text-white rounded-xl text-[10px] font-black uppercase tracking-wider transition-all disabled:opacity-30 outline-none"
            >
              Step Forward
            </button>
          </div>

          <div className="flex items-center gap-4 w-full sm:w-64">
            <span className="text-[10px] font-black uppercase text-neutral-500 tracking-wider whitespace-nowrap">Speed:</span>
            <input 
              type="range"
              min="300"
              max="2000"
              step="100"
              value={speed}
              onChange={e => setSpeed(parseInt(e.target.value))}
              className="w-full accent-red-600 cursor-pointer h-1 bg-neutral-800 rounded-lg appearance-none"
            />
            <span className="text-xs font-bold text-neutral-400 font-mono">{(speed/1000).toFixed(1)}s</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Share2Icon: React.FC<{ size?: number; className?: string }> = ({ size = 16, className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

export default TradesDemo;
